import React, { useState, useCallback } from 'react';
import { Menu, MenuItem } from '@mui/material';
import { WBIconButton, WBSvgIcon } from '@admiin-com/ds-web';
import FontColorIcon from '../../../../../assets/icons/document-editor/font-color.svg';
import BgColorIcon from '../../../../../assets/icons/document-editor/bg-color.svg';
import { ColorResult } from 'react-color';
import { ColorPicker } from '../../../../components/ColorPicker/ColorPicker';
import { LexicalEditor } from 'lexical';
import { $getSelection, $isRangeSelection } from 'lexical';
import { $patchStyleText } from '@lexical/selection';
import { VerticalDivider } from '../DocumentEditor.styles';

interface ColorControlsProps {
  editor: LexicalEditor;
  fontColor: string;
  bgColor: string;
  setFontColor: (color: string) => void;
  setBgColor: (color: string) => void;
}

export const ColorControls: React.FC<ColorControlsProps> = ({
  editor,
  fontColor,
  bgColor,
  setFontColor,
  setBgColor,
}) => {
  const [colorMenuAnchor, setColorMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [bgColorMenuAnchor, setBgColorMenuAnchor] =
    useState<null | HTMLElement>(null);

  const applyTextColor = useCallback(
    (color: string) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $patchStyleText(selection, {
            color: color,
          });
        }
      });
    },
    [editor]
  );

  const applyBgColor = useCallback(
    (color: string) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $patchStyleText(selection, {
            'background-color': color,
          });
        }
      });
    },
    [editor]
  );

  const handleTextColorChange = useCallback(
    (colorResult: ColorResult) => {
      const color = colorResult.hex;
      setFontColor(color);
      applyTextColor(color);
      setColorMenuAnchor(null);
    },
    [applyTextColor, setFontColor]
  );

  const handleBgColorChange = useCallback(
    (colorResult: ColorResult) => {
      const color = colorResult.hex;
      setBgColor(color);
      applyBgColor(color);
      setBgColorMenuAnchor(null);
    },
    [applyBgColor, setBgColor]
  );

  return (
    <>
      <WBIconButton
        size="small"
        onClick={(event) => setColorMenuAnchor(event.currentTarget)}
        title="Text Color"
        sx={{
          color: 'text.primary',
        }}
      >
        <WBSvgIcon fontSize="small">
          <FontColorIcon />
        </WBSvgIcon>
      </WBIconButton>
      <Menu
        anchorEl={colorMenuAnchor}
        open={Boolean(colorMenuAnchor)}
        onClose={() => setColorMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <MenuItem>
          <ColorPicker color={fontColor} onChange={handleTextColorChange} />
        </MenuItem>
      </Menu>
      <WBIconButton
        size="small"
        onClick={(event) => setBgColorMenuAnchor(event.currentTarget)}
        title="Background Color"
        sx={{
          color: 'text.primary',
        }}
      >
        <WBSvgIcon fontSize="small">
          <BgColorIcon />
        </WBSvgIcon>
      </WBIconButton>
      <Menu
        anchorEl={bgColorMenuAnchor}
        open={Boolean(bgColorMenuAnchor)}
        onClose={() => setBgColorMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <MenuItem>
          <ColorPicker color={bgColor} onChange={handleBgColorChange} />
        </MenuItem>
      </Menu>

      <VerticalDivider orientation="vertical" flexItem />
    </>
  );
};
